import type {
  CastaiAutoscalerV1beta1RebalancingNode,
  CastaiAutoscalerV1beta1ExecutionConditions,
  CastaiAutoscalerV1beta1AggressiveModeConfig,
  CastaiAutoscalerV1beta1RebalancingPlanResponse,
  CastaiAutoscalerV1beta1RebalancingPlanResponseOperation,
  CastaiAutoscalerV1beta1RebalancingPlanResponsePlanError,
  CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurations,
} from '@cast/openapi';
import type { RebalancingPlan as KentRebalancingPlan } from '@cast/openapi-kent';
import {
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkload,
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadNode,
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponse,
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadNodeNotReadyStatusReason,
} from '@cast/openapi';
import {
  Workload as KentRebalanceWorkload,
  WorkloadNode as KentRebalanceWorkloadNode,
  Issue as KentIssue,
  AggressiveModeConfig as KentAggressiveModeConfig,
} from '@cast/openapi-kent';

export type {
  CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurationsConfigurationNode as RebalancingNode,
  CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurationsConfiguration as RebalancingConfiguration,
  CastaiAutoscalerV1beta1RebalancingPlanResponsePlanError as RebalancingPlanError,
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadIssue as WorkloadIssue,
  CastaiAutoscalerV1beta1ListRebalancingPlansResponse as RebalancingPlansResponse,
  CastaiAutoscalerV1beta1RebalancingPlanResponseOperation as RebalancingPlanOperation,
  CastaiAutoscalerV1beta1RebalancingPlanResponse as AutoscalerRebalancingPlan,
  CastaiAutoscalerV1beta1GenerateRebalancingPlanResponse as RebalancingAccepted,
  CastaiAutoscalerV1beta1AggressiveModeConfig as AggressiveModeConfig,
  CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurationsDiff as RebalancingPlanConfigurationsDiff,
  CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurationsConfigurationProvisionedNode as RebalancingPlanProvisionedNode,
  AutoscalerAPIGenerateRebalancingPlanRequest as GenerateRebalancingPlanRequest,
} from '@cast/openapi';

export {
  CastaiAutoscalerV1beta1RebalancingPlanResponseOperationType as RebalancingPlanOperationTypeEnum,
  CastaiAutoscalerV1beta1Status as RebalancingPlanStatusEnum,
  CastaiAutoscalerV1beta1Os as OSEnum,
  CastaiAutoscalerV1beta1RebalancingPlanResponsePlanErrorReason as RebalanceErrorStatus,
} from '@cast/openapi';

export {
  RebalancingPlanStatusEnum as KentRebalancingPlanStatusEnum,
  RebalancingErrorCodeEnum as KentRebalanceErrorStatus,
} from '@cast/openapi-kent';
export type {
  RebalancingPlan as KentRebalancingPlan,
  LongRunningOperation as KentRebalancingAccepted,
  CreateRebalancingPlanRequest as GenerateKentRebalancingPlanParams,
} from '@cast/openapi-kent';

export type GenerateRebalancingPlanParams = {
  evictGracefully?: boolean | null;
  aggressiveMode?: boolean | null;
  aggressiveModeConfig?: CastaiAutoscalerV1beta1AggressiveModeConfig;
  executionConditions?: CastaiAutoscalerV1beta1ExecutionConditions;
  keepDrainTimeoutNodes?: boolean | null;
  minNodes?: number;
  maxSimultaneousDrains?: number | null;
  splitNodesInBatchesOf?: number | null;
  rebalancingNodes?: Array<CastaiAutoscalerV1beta1RebalancingNode>;
};

type KentSpecificGenerateRebalancingPlanParams = {
  kentAggressiveModeConfig?: KentAggressiveModeConfig;
  rebalancingNodeNames?: Array<{ nodeName: string }>;
};

export type GenerateRebalancingPlanCombinedParams =
  GenerateRebalancingPlanParams & KentSpecificGenerateRebalancingPlanParams;

export enum WorkloadCostImpactLevelEnum {
  low = 'low',
  medium = 'medium',
  high = 'high',
}

export enum WorkloadMigrationStatusMigrationStatusEnum {
  ready = 'ready',
  not_ready = 'not-ready',
}

export enum NodeMigrationStatusMigrationStatusEnum {
  ready = 'ready',
  not_ready = 'not-ready',
}

export type WorkloadNode = Omit<
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadNode,
  'status'
> & {
  status?: Omit<
    CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadNode['status'],
    'migrationStatus' | 'reason'
  > & {
    migrationStatus?: NodeMigrationStatusMigrationStatusEnum;
    issues?: KentIssue[];
    reason?: CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkloadNodeNotReadyStatusReason;
  };
};

type KentWorkloadNode = KentRebalanceWorkloadNode & {
  status?: {
    migrationStatus?: NodeMigrationStatusMigrationStatusEnum;
  };
};

export type Workload = Omit<
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkload,
  'status' | 'nodes' | 'costImpact'
> & {
  status?: { migrationStatus?: WorkloadMigrationStatusMigrationStatusEnum };
  nodes?: WorkloadNode[];
  costImpact?: CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkload['costImpact'] & {
    level?: WorkloadCostImpactLevelEnum;
  };
};

export type KentWorkload = Omit<
  KentRebalanceWorkload,
  'status' | 'nodes' | 'costImpact'
> & {
  status?: { migrationStatus?: WorkloadMigrationStatusMigrationStatusEnum };
  nodes?: KentWorkloadNode[];
  costImpact?: CastaiAutoscalerV1beta1GetClusterWorkloadsResponseWorkload['costImpact'] & {
    level?: WorkloadCostImpactLevelEnum;
  };
};

export type ClusterWorkloads = Omit<
  CastaiAutoscalerV1beta1GetClusterWorkloadsResponse,
  'workloads'
> & {
  workloads?: Workload[];
};

type NormalizedRebalancingPlanBase = {
  rebalancingPlanId?: string;
  clusterId: string;
  scheduleId?: string;
  configurations: CastaiAutoscalerV1beta1RebalancingPlanResponseConfigurations;
  operations: CastaiAutoscalerV1beta1RebalancingPlanResponseOperation[];
  errors?: CastaiAutoscalerV1beta1RebalancingPlanResponsePlanError[];
  rebalancingNodeIds: string[];
  createdAt: string;
  updatedAt: string;
  generatedAt?: string;
  triggeredAt?: string;
  finishedAt?: string;
};

export type NormalizedAutoscalerRebalancingPlan =
  NormalizedRebalancingPlanBase & {
    variant: 'autoscaler';
    rawPlan: CastaiAutoscalerV1beta1RebalancingPlanResponse;
  };

export type NormalizedKentRebalancingPlan = NormalizedRebalancingPlanBase & {
  variant: 'kent';
  rawPlan: KentRebalancingPlan;
};

export type NormalizedRebalancingPlan =
  | NormalizedAutoscalerRebalancingPlan
  | NormalizedKentRebalancingPlan;
